<!-- ============================================================================ -->
<!-- TASK ROW — Ligne de tâche réutilisable (mode normal 44px / compact 36px)  -->
<!-- ============================================================================ -->
<div
  class="task-row"
  [class.task-row--compact]="compact"
  [class.task-row--done]="isDone()"
  [class.task-row--in-progress]="isInProgress()"
  [class.task-row--overdue]="isOverdue()"
  [class.task-row--selected]="selected"
  [class.task-row--selection-mode]="selectionMode"
  (click)="onRowClick($event)"
  (contextmenu)="onContextMenu($event)"
  role="listitem"
>
  <!-- Poignée de glisser (visible au survol) : seule zone qui déplace la ligne -->
  <ng-icon
    name="heroBars3"
    class="task-row__drag-handle"
    *ngIf="showDragHandle"
    cdkDragHandle
    aria-hidden="true"
    (click)="$event.stopPropagation()"
  ></ng-icon>

  <!-- Checkbox circulaire — cycle TODO → IN_PROGRESS → DONE → TODO -->
  <div
    class="task-row__checkbox"
    [class.task-row__checkbox--done]="isDone()"
    [class.task-row__checkbox--in-progress]="isInProgress()"
    (click)="onCheckboxClick($event)"
    role="checkbox"
    [attr.aria-checked]="isDone()"
    [attr.aria-label]="getCheckboxAriaLabel()"
    tabindex="0"
    (keydown.enter)="onCheckboxClick($event)"
    (keydown.space)="onCheckboxClick($event)"
  >
    @if (isDone()) {
      <!-- Statut DONE : coche blanche -->
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="task-row__check-icon"
      >
        <path d="M2 6L5 9L10 3" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    } @else if (isInProgress()) {
      <!-- Statut IN_PROGRESS : icône play -->
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="task-row__check-icon"
      >
        <path d="M3 2L8 5L3 8V2Z" fill="currentColor" />
      </svg>
    }
  </div>

  <!-- Titre + % avancement -->
  <div class="task-row__title">
    <span class="task-row__title-text" (click)="onTitleClick($event)">{{ task?.title }}</span>
    @if (task?.comment) {
      <span class="task-row__comment-icon" [evaTooltip]="task.comment">
        <ng-icon name="heroChatBubbleLeft"></ng-icon>
      </span>
    }
    @if (showProgress) {
      <span class="task-row__progress-badge" [class.task-row__progress-badge--done]="isDone()">
        {{ isDone() ? "100" : "0" }}%
      </span>
    }
  </div>

  <!-- =========================================================== -->
  <!-- MODE NORMAL — tout visible, menu au hover                   -->
  <!-- =========================================================== -->
  @if (!compact) {
    <!-- Chips catégories (toujours visibles) -->
    @for (cat of getTaskCategories(); track cat.category_id) {
      <span
        class="task-row__category-chip"
        [style.background-color]="cat.color + '22'"
        [style.color]="cat.color"
        [style.border-color]="cat.color + '44'"
      >
        {{ cat.name }}
      </span>
    }

    <!-- Tags objets liés -->
    @if (showObjectLink && getObjectLinks().length > 0) {
      @for (link of getObjectLinks().slice(0, 2); track link.object_id) {
        @if (getObjectLinkRoute(link); as route) {
          <a
            class="task-row__object-link"
            [routerLink]="route"
            [attr.aria-label]="'Ouvrir ' + link.object_name"
            (click)="$event.stopPropagation()"
          >
            <eva-tag [preset]="getObjectLinkPreset(link)" size="small" [rounded]="true" />
          </a>
        } @else {
          <eva-tag [preset]="getObjectLinkPreset(link)" size="small" [rounded]="true" />
        }
      }
      @if (getObjectLinks().length > 2) {
        <span class="task-row__object-tag task-row__object-tag--more">+{{ getObjectLinks().length - 2 }}</span>
      }
    }

    <!-- Badge date — 3 modes : aucune / échéance / période -->
    @if (showDate && getDateMode() === "deadline") {
      <div
        class="task-row__date-badge"
        [class.task-row__date-badge--overdue]="isOverdue()"
        [class.task-row__date-badge--today]="isToday(task.due_date)"
      >
        {{ formatDueDate(task.due_date) }}
      </div>
    }
    @if (showDate && getDateMode() === "range") {
      <div class="task-row__date-badge task-row__date-badge--range" [class.task-row__date-badge--overdue]="isOverdue()">
        <ng-icon name="heroCalendar" style="font-size: 0.65rem; margin-right: 3px"></ng-icon>{{ formatRangeDate() }}
      </div>
    }

    <!-- Badge urgence (affiché uniquement si non "Normale") -->
    @if (task?.urgency && task.urgency !== "NORMAL") {
      <span
        class="task-row__urgency-badge"
        [class.task-row__urgency-badge--urgent]="isUrgent()"
        [class.task-row__urgency-badge--low]="isLowPriority()"
      >
        {{ getUrgencyLabel() }}
      </span>
    }

    <!-- Icône privée (juste avant les avatars) -->
    @if (task?.is_private) {
      <span class="task-row__private-icon" evaTooltip="Tâche privée">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="5" width="8" height="6" rx="1" stroke="currentColor" stroke-width="1.2" />
          <path d="M4 5V3.5a2 2 0 1 1 4 0V5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
        </svg>
      </span>
    }

    <!-- Badge commentaires -->
    @if (task?.comments_count > 0) {
      <span class="task-row__comments-badge">
        <ng-icon name="heroChatBubbleLeft"></ng-icon> {{ task.comments_count }}
      </span>
    }

    <!-- Avatars assignés (toujours à droite) -->
    @if (showAssignees && task?.assigned_to?.length > 0) {
      <div class="task-row__avatars">
        @for (assignee of getVisibleAssignees(); track $index) {
          <div
            class="task-row__avatar"
            [style.background-color]="getAssigneeColor(assignee)"
            [title]="getAssigneeTooltip(assignee)"
          >
            {{ getAssigneeInitials(assignee) }}
          </div>
        }
      </div>
    }

    <!-- Menu ⋮ (visible au survol) -->
    <div class="task-row__menu task-row__hover-action" (click)="$event.stopPropagation()">
      <button type="button" class="task-row__menu-btn" (click)="toggleRowMenu($event)">
        <ng-icon name="heroEllipsisVertical"></ng-icon>
      </button>
      @if (showRowMenu) {
        <div
          class="task-row__menu-dropdown"
          [style.top.px]="menuOpensUpward ? null : menuPosition.top"
          [style.bottom.px]="menuOpensUpward ? menuPosition.bottom : null"
          [style.right.px]="menuPosition.right"
          [style.visibility]="menuPlaced ? null : 'hidden'"
        >
          <button type="button" class="task-row__menu-item" (click)="onMenuAction('edit', $event)">
            <ng-icon name="heroPencil"></ng-icon>
            Modifier
          </button>
          <button
            type="button"
            class="task-row__menu-item task-row__menu-item--danger"
            (click)="onMenuAction('delete', $event)"
          >
            <ng-icon name="heroTrash"></ng-icon>
            Supprimer
          </button>
        </div>
      }
    </div>
  }

  <!-- =========================================================== -->
  <!-- MODE COMPACT — tag + date toujours visibles, menu au hover  -->
  <!-- =========================================================== -->
  @if (compact) {
    <!-- Badge date — 3 modes : aucune / échéance / période -->
    @if (showDate && getDateMode() === "deadline") {
      <div
        class="task-row__date-badge"
        [class.task-row__date-badge--overdue]="isOverdue()"
        [class.task-row__date-badge--today]="isToday(task.due_date)"
      >
        {{ formatDueDate(task.due_date) }}
      </div>
    }
    @if (showDate && getDateMode() === "range") {
      <div class="task-row__date-badge task-row__date-badge--range" [class.task-row__date-badge--overdue]="isOverdue()">
        <ng-icon name="heroCalendar" style="font-size: 0.65rem; margin-right: 3px"></ng-icon>{{ formatRangeDate() }}
      </div>
    }
  }
</div>
